import { type FC } from 'react';
import cn from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { useAppSelector } from '../hooks/useReactReduxHooks';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { PdpRespSidebarDetailsSection } from './PdpRespSidebarDetailsSection';
import { PdpSharedDetailShipping } from '../PdpSharedDetails/PdpSharedDetailShipping';
import { PdpSharedDetailReturnPolicy } from '../PdpSharedDetails/PdpSharedDetailReturnPolicy';

// types
import { type PdpRespSidebarDetailsShipping_item$key as Item } from './__generated__/PdpRespSidebarDetailsShipping_item.graphql';
import { type PdpRespSidebarDetailsShipping_user$key as User } from './__generated__/PdpRespSidebarDetailsShipping_user.graphql';

// styles
import dibsCss from 'dibs-css';

const Shipping = (
    <FormattedMessage
        id="PdpRespSidebarDetailsAbout.shipping"
        defaultMessage="Shipping and Returns"
    />
);

const userFragment = graphql`
    fragment PdpRespSidebarDetailsShipping_user on User {
        serviceId
        ...PdpSharedDetailShipping_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespSidebarDetailsShipping_item on Item {
        serviceId
        ...PdpSharedDetailShipping_item
        ...PdpSharedDetailReturnPolicy_item
    }
`;

export const PdpRespSidebarDetailsShipping: FC<{
    item: Item;
    user: User | null;
    itemQuantity: number | null;
}> = ({ item: itemRef, user: userRef, itemQuantity }) => {
    const { currency } = useCurrency();
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});
    const userId = user?.serviceId;

    if (!item) {
        return null;
    }

    return (
        <PdpRespSidebarDetailsSection
            title={Shipping}
            ga={{ trigger: 'shipping, returns & payment' }}
        >
            <div className={dibsCss.sassyFontBodySmall}>
                <PdpSharedDetailShipping
                    item={item}
                    displayInline
                    userId={userId}
                    user={user || null}
                    currency={currency}
                    isClient={!!afterDisplay}
                    itemQuantity={itemQuantity}
                    preLinkQuoteDetails={null}
                    postLinkQuoteDetails={null}
                    postLinkNoQuoteDetails={null}
                />
                {item.serviceId && (
                    <div className={cn(dibsCss.flex, dibsCss.flexCol)}>
                        <span className={dibsCss.sassyFontBodySmallHeavy}>
                            <FormattedMessage
                                id="PdpRespSidebarDetailsAbout.returns"
                                defaultMessage="Return Policy"
                            />
                        </span>
                        <PdpSharedDetailReturnPolicy
                            item={item}
                            itemId={item.serviceId}
                            userId={userId}
                        />
                    </div>
                )}
            </div>
        </PdpRespSidebarDetailsSection>
    );
};
